import { useState, useEffect, useRef, useMemo } from "react";
import { FixedSizeList as List } from "react-window";
import { baseColourClasses, Button, StoreNames } from "../components/ui/button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "../components/card";
import { NewsletterForm } from "./newsletter-form";
import { Link } from "../components/ui/link";
import { formatDaysDuration, onlyUnique } from "./pageUtils";
import { MultiselectDropdown } from "../components/ui/multiSelectDropdown";

export interface Grocery {
    offer_id: number;
    index: number;
    selected: boolean;
    heading: string;
    dealer_name: StoreNames;
    ner_product_entities: string[];
    avg_days_between_sales: number | null;
}

export default function Home() {
    const stores: { storeName: StoreNames }[] = [
        { storeName: "KIWI" },
        { storeName: "Coop Extra" },
        { storeName: "Coop Prix" },
        {
            storeName: "Coop Mega",
        },
        { storeName: "Obs" },
        { storeName: "MENY" },
        { storeName: "Bunnpris" },
        { storeName: "Europris" },
        { storeName: "REMA 1000" },
        { storeName: "SPAR" },
        { storeName: "Joker" },
    ];
    const [selectedStores, setSelectedStores] = useState<string[]>([]);
    const [groceries, setGroceries] = useState<Grocery[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>("");
    // Used for filtering offers by product
    const productEntitiesSet = useMemo(() => {
        const uniqueEntities = new Set<string>();
        groceries.forEach((g: Grocery) => {
            g.ner_product_entities?.forEach((p: string) =>
                uniqueEntities.add(p),
            );
        });
        return uniqueEntities;
    }, [groceries]);
    const [selectedProducts, setSelectedProducts] = useState<Set<string>>(
        new Set(),
    );

    // Sorted groceries list with selected at top of list
    const [sortedGroceries, setSortedGroceries] =
        useState<Grocery[]>(groceries);
    const selectedGroceries = sortedGroceries.filter(
        (grocery: Grocery) => grocery.selected,
    );
    const selectedGroceryIds = selectedGroceries.map(
        (grocery) => grocery.offer_id,
    );
    const dynamicSelectedStores = selectedGroceries
        .map((grocery) => grocery.dealer_name)
        .filter(onlyUnique)
        .map((dealerName) => dealerName);

    // Fetch groceries from local API
    useEffect(() => {
        // Construct API URL
        let apiUrl = "/api/allUniqueOffers";
        if (selectedStores.length > 0) {
            const storesParam = selectedStores
                .map(encodeURIComponent)
                .join(",");
            apiUrl += `?store_filters=${storesParam}`;
        }

        setLoading(true);
        setError(null);

        fetch(apiUrl)
            .then((response) => {
                if (!response.ok) throw new Error("Failed to fetch groceries");
                return response.json();
            })
            .then((data) => {
                const groceriesWithSelection = data
                    .filter((item: Grocery) => item.heading !== "")
                    .map((item: Grocery, index: number) => ({
                        ...item,
                        index,
                        selected: false,
                    }));

                setGroceries(groceriesWithSelection);
                setSortedGroceries(
                    [...groceriesWithSelection].sort((a, b) =>
                        a.selected === b.selected
                            ? a.heading.localeCompare(b.heading)
                            : a.selected
                              ? -1
                              : 1,
                    ),
                );

                setLoading(false);
            })
            .catch((error) => {
                console.error("Offers failed to load", error);
                setError(error.message);
                setLoading(false);
            });
    }, [selectedStores]);

    const toggleGrocery = (index: number) => {
        setGroceries((prev) =>
            prev.map((g) =>
                g.index === index ? { ...g, selected: !g.selected } : g,
            ),
        );
    };

    useEffect(() => {
        if (groceries.length) {
            let updatedGroceries = [...groceries];

            // Filter groceries by selectedProducts
            if (selectedProducts.size > 0) {
                updatedGroceries = updatedGroceries.filter((grocery) =>
                    grocery.ner_product_entities.some((product) =>
                        selectedProducts.has(product),
                    ),
                );
            }

            // Sort by search term
            updatedGroceries.sort((a, b) => {
                if (searchTerm.trim() === "") {
                    return a.heading.localeCompare(b.heading);
                } else {
                    const lowerSearch = searchTerm.toLowerCase();
                    const aIncludes = a.heading
                        .toLowerCase()
                        .includes(lowerSearch);
                    const bIncludes = b.heading
                        .toLowerCase()
                        .includes(lowerSearch);

                    if (aIncludes && !bIncludes) return -1;
                    if (!aIncludes && bIncludes) return 1;

                    return a.heading.localeCompare(b.heading);
                }
            });

            // Selected groceries to the top
            updatedGroceries.sort((a, b) =>
                a.selected === b.selected ? 0 : a.selected ? -1 : 1,
            );

            setSortedGroceries(updatedGroceries);
        }
    }, [groceries, searchTerm, selectedProducts]);

    const Row = ({
        index,
        style,
    }: {
        index: number;
        style: React.CSSProperties;
    }) => {
        const grocery = sortedGroceries[index];

        return (
            <div
                style={style}
                className={`flex items-center ${
                    grocery?.selected
                        ? "bg-indigo-400"
                        : baseColourClasses[grocery?.dealer_name]
                } ${index !== 0 ? "border-t border-black" : ""}`}
                onClick={() => toggleGrocery(grocery?.index)}
            >
                <span className="p-2 w-1/3 text-left truncate">
                    {grocery?.dealer_name}
                </span>

                <span className="p-2 w-1/3 text-left truncate">
                    {grocery?.heading}
                </span>
                <div className="p-2 w-1/3 flex justify-end">
                    {grocery?.avg_days_between_sales != null &&
                        grocery.avg_days_between_sales > 0 && (
                            <span className="inline-block rounded-full bg-gray-100 text-black px-3 py-1 text-sm font-medium">
                                {formatDaysDuration(
                                    grocery.avg_days_between_sales,
                                )}
                            </span>
                        )}
                </div>
            </div>
        );
    };

    return (
        <main className="min-h-screen flex flex-col relative">
            <section className="mt-8 mb-4">
                <div className="container mx-auto px-4">
                    <h1 className="text-4xl font-bold">SalgScout</h1>
                    <nav>
                        <Link to="#about">Om SalgScout</Link>
                        <span> - </span>
                        <Link to="#select-groceries" className="mr-4">
                            Velg Dagligvaretilbud
                        </Link>
                        <span> - </span>
                        <Link to="#subscribe">Få Ukentlige Tilbudsvarsler</Link>
                    </nav>
                </div>
            </section>

            <section id="about" className="my-2 flex-grow">
                <div className="container mx-auto py-4 my-2">
                    <Card>
                        <CardHeader>
                            <CardTitle>
                                Lei av å sjekke alle tilbudsavisene?
                            </CardTitle>
                            <CardDescription>
                                Få ukentlige e-poster med akkurat de tilbudene
                                du vil ha – uten å måtte lese hundrevis av
                                tilbudsaviser.
                            </CardDescription>
                        </CardHeader>
                    </Card>
                </div>
            </section>

            <section id="select-groceries" className="py-4 flex-grow">
                <div className="container mx-auto py-4 my-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Velg Butikker</CardTitle>
                            <CardDescription>
                                Velg butikker for ukentlige tilbudsvarsler.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-2">
                                {stores.map((store) => (
                                    <Button
                                        label={store.storeName}
                                        key={store.storeName}
                                        selected={selectedStores.includes(
                                            store.storeName,
                                        )}
                                        colour={store.storeName}
                                        onClick={(e) =>
                                            setSelectedStores((prev) =>
                                                prev.includes(store.storeName)
                                                    ? prev.filter(
                                                          (s) =>
                                                              s !==
                                                              store.storeName,
                                                      )
                                                    : [
                                                          ...prev,
                                                          store.storeName,
                                                      ],
                                            )
                                        }
                                        dimUnselected={
                                            selectedStores.length > 0
                                        }
                                    />
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="container mx-auto py-4 my-4">
                    <Card>
                        <CardHeader>
                            <CardTitle>Velg Dagligvaretilbud</CardTitle>
                            <CardDescription>
                                Søk etter og legg til dagligvaretilbud du vil
                                følge med på.
                                <br></br>
                                Valgte tilbud: {selectedGroceries.length}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex justify-between mb-1">
                                <div>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) =>
                                            setSearchTerm(e.target.value)
                                        }
                                        placeholder="Søk etter beskrivelse..."
                                        className="p-2 m-auto border rounded-md"
                                    />
                                </div>
                                <MultiselectDropdown
                                    options={productEntitiesSet}
                                    selectedOptions={selectedProducts}
                                    onSelectionChange={setSelectedProducts}
                                />
                            </div>
                            <div className="border rounded-md bg-white overflow-hidden">
                                <div className="flex items-center border-b font-semibold">
                                    <span className="p-2 w-1/3">Butikk</span>
                                    <span className="p-2 w-1/3">Tilbud</span>
                                    <span className="p-2 w-1/3 text-right">
                                        Tid Mellom Tilbud
                                    </span>
                                </div>

                                {loading ? (
                                    <div className="p-4 text-center">
                                        Laster...
                                    </div>
                                ) : error ? (
                                    <div className="p-4 text-red-500">
                                        {error}
                                    </div>
                                ) : (
                                    <List
                                        height={320}
                                        itemCount={sortedGroceries.length}
                                        itemSize={40}
                                        width="100%"
                                    >
                                        {Row}
                                    </List>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>

            <section id="subscribe" className="pb-16">
                <div className="container mx-auto">
                    <Card>
                        <CardHeader>
                            <CardTitle>Få Ukentlige Tilbudsvarsler</CardTitle>
                            <CardDescription>
                                Meld deg på for å bli tilsendt e-post hver
                                mandag som informerer deg om tilbudene du har
                                valgt å følge med på.
                                <br></br>
                                Valgte butikker:{" "}
                                {dynamicSelectedStores.length
                                    ? dynamicSelectedStores.length
                                    : "Ingen"}
                                <br></br>
                                Valgte tilbud: {selectedGroceries.length}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <NewsletterForm
                                selectedGroceriesCount={
                                    groceries.filter((g) => g.selected).length
                                }
                                selectedGroceryIds={selectedGroceryIds}
                            />
                        </CardContent>
                    </Card>
                </div>
            </section>
        </main>
    );
}
